import { useState } from "react";
import { X, Copy, Check, Linkedin, MessageCircle, Mail, Twitter, Share2, Send } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

/**
 * Post-signup "share your affiliate link" modal.
 *
 * Shown automatically the first time an affiliate lands on the panel after
 * opting in. Also reopenable manually via the "Deel opnieuw" button in the
 * opted-in panel. Provides one-click share to LinkedIn, WhatsApp, X and
 * email with pre-composed NL/EN messaging so time-to-first-click is near
 * zero.
 */
export default function AffiliateShareModal({ open, onClose, refCode }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  if (!open || !refCode) return null;

  const shareUrl = `${window.location.origin}/api/r/${refCode}`;
  const message = t("affShareMessage");
  const subject = t("affShareEmailSubject");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("affLinkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("affLinkCopyFailed"));
    }
  };

  const sendInvite = async () => {
    const recipient = inviteEmail.trim();
    if (!recipient) return;
    setSending(true);
    try {
      const r = await api.post("/affiliate/invite", {
        recipient_email: recipient,
        note: inviteNote.trim(),
      });
      setSentTo(r.data?.recipient || recipient);
      toast.success(t("affInviteToastSent"));
      setInviteEmail("");
      setInviteNote("");
      // Clear the "sent" badge after 8 seconds to avoid stale UI.
      setTimeout(() => setSentTo(""), 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("affInviteToastFailed"));
    } finally {
      setSending(false);
    }
  };

  // Pre-composed intent URLs. `encodeURIComponent` guards against locales
  // whose translated message contains ampersands, quotes, or emoji.
  const channels = [
    {
      key: "linkedin",
      Icon: Linkedin,
      label: t("affShareLinkedIn"),
      // LinkedIn's sharing URL only reliably picks up the URL; the composed
      // text is copied to the clipboard so the user can paste it as post
      // body — LinkedIn dropped `summary` from `sharing/share-offsite`.
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(`${message}\n\n${shareUrl}`);
        } catch { /* ignore — the share window still opens */ }
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          "_blank",
          "noopener,noreferrer,width=640,height=640"
        );
      },
      classes: "bg-[#0A66C2] text-white hover:bg-[#004182]",
    },
    {
      key: "whatsapp",
      Icon: MessageCircle,
      label: t("affShareWhatsApp"),
      onClick: () => {
        const text = `${message} ${shareUrl}`;
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text)}`,
          "_blank",
          "noopener,noreferrer"
        );
      },
      classes: "bg-[#25D366] text-white hover:bg-[#128C7E]",
    },
    {
      key: "twitter",
      Icon: Twitter,
      label: "X / Twitter",
      onClick: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}`,
          "_blank",
          "noopener,noreferrer,width=640,height=520"
        );
      },
      classes: "bg-[#1A1A1A] text-white hover:bg-black",
    },
    {
      key: "email",
      Icon: Mail,
      label: t("affShareEmail"),
      onClick: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${message}\n\n${shareUrl}`)}`;
      },
      classes: "bg-neutral-200 text-neutral-900 hover:bg-neutral-300",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="affiliate-share-modal">
      <div className="bg-[#F9F8F6] w-full max-w-lg border border-neutral-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-900 px-6 py-4">
          <p className="ed-eyebrow flex items-center gap-2">
            <Share2 size={12} strokeWidth={1.5} />
            {t("affShareModalEyebrow")}
          </p>
          <button
            data-testid="affiliate-share-modal-close"
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-neutral-100"
            aria-label={t("common_close") || "Close"}
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <h3 className="font-serif text-2xl leading-tight">{t("affShareModalTitle")}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed mt-2">
              {t("affShareModalBody")}
            </p>
          </div>

          {/* Copyable link row */}
          <div>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
              {t("affShareYourLink")}
            </p>
            <div className="flex items-stretch gap-2">
              <input
                data-testid="affiliate-share-link-input"
                readOnly
                value={shareUrl}
                onClick={(e) => e.target.select()}
                className="flex-1 border border-neutral-300 px-3 py-2 font-mono text-xs text-neutral-700 bg-white focus:border-neutral-900 outline-none min-w-0"
              />
              <button
                data-testid="affiliate-share-copy-btn"
                onClick={copy}
                className="ed-btn shrink-0 inline-flex items-center gap-1.5"
              >
                {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.5} />}
                {copied ? t("affCopied") : t("affCopy")}
              </button>
            </div>
          </div>

          {/* Channels grid */}
          <div>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
              {t("affShareChannels")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {channels.map(({ key, Icon, label, onClick, classes }) => (
                <button
                  key={key}
                  data-testid={`affiliate-share-${key}`}
                  onClick={onClick}
                  className={`px-4 py-3 font-mono text-xs uppercase tracking-[0.12em] transition-colors inline-flex items-center justify-center gap-2 ${classes}`}
                >
                  <Icon size={14} strokeWidth={1.75} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Send an actual email via JobGrid Resend (not mailto). */}
          <div className="border-t border-neutral-200 pt-5">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
              {t("affInviteEyebrow")}
            </p>
            <p className="text-sm text-neutral-700 leading-relaxed mb-3">
              {t("affInviteBody")}
            </p>
            <div className="flex flex-col gap-2">
              <input
                data-testid="affiliate-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="voorbeeld@example.com"
                className="w-full border border-neutral-300 px-3 py-2 font-mono text-sm focus:border-neutral-900 outline-none"
              />
              <textarea
                data-testid="affiliate-invite-note"
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value.slice(0, 280))}
                placeholder={t("affInviteNotePlaceholder")}
                rows={2}
                className="w-full border border-neutral-300 px-3 py-2 font-serif text-sm focus:border-neutral-900 outline-none resize-none"
              />
              <div className="flex items-center justify-between">
                <span className="font-mono text-[0.6rem] text-neutral-500">
                  {inviteNote.length}/280 · {t("affInviteOptionalNote")}
                </span>
                <button
                  data-testid="affiliate-invite-send"
                  onClick={sendInvite}
                  disabled={sending || !inviteEmail.trim()}
                  className="ed-btn inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={12} strokeWidth={1.75} />
                  {sending ? t("affInviteSending") : t("affInviteSend")}
                </button>
              </div>
              {sentTo && (
                <p
                  data-testid="affiliate-invite-sent"
                  className="mt-1 inline-flex items-center gap-1.5 font-mono text-[0.65rem] text-emerald-800"
                >
                  <Check size={11} strokeWidth={2.5} className="text-emerald-600" />
                  {t("affInviteSent", { recipient: sentTo })}
                </p>
              )}
            </div>
          </div>

          <p className="text-xs text-neutral-500 font-mono leading-relaxed">
            {t("affShareTip")}
          </p>
        </div>

        <div className="border-t border-neutral-200 px-6 py-3 flex justify-end">
          <button
            data-testid="affiliate-share-done"
            onClick={onClose}
            className="ed-btn ed-btn-outline"
          >
            {t("affShareDone")}
          </button>
        </div>
      </div>
    </div>
  );
}
